import React from 'react'
import styled from 'styled-components'
import Card from '@jarvis.oddle/core/components/Card'
import Div from '@jarvis.oddle/core/components/Div'
import Flex from '@jarvis.oddle/core/components/Flex'
import Form from '@jarvis.oddle/core/components/Form'
import IconDelete from '@jarvis.oddle/core/components/Icons/IconDelete'
import DivButton from '@jarvis.oddle/core/components/DivButton'
import { H4 } from '@jarvis.oddle/core/components/Typo'
import {
  borderRadiusSizes,
  iconSizes,
  marginSizes,
  paddingSizes,
  typo
} from '@jarvis.oddle/core/theme/default'
import IconCard from '@jarvis.oddle/core/components/Icons/IconCard'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import { getCreditCardInfo } from 'src/utils/payment'
import CreditCardSkeleton from './Skeleton'

const DefaultContainer = styled(Card).attrs(() => ({
  borderRadius: borderRadiusSizes.small,
  paddingLeft: paddingSizes.medium,
  paddingRight: paddingSizes.medium,
  paddingTop: paddingSizes.small,
  paddingBottom: paddingSizes.small
}))`
  display: flex;
  flex-direction: row;
  align-items: center;

  &:hover,
  &:focus {
    cursor: pointer;
    pointer-events: auto;
  }
`

const EndRightContainer = styled(Flex).attrs(() => ({
  flexDirection: 'column',
  justifyContent: 'flex-end',
  alignItems: 'center'
}))``

const StyledLabel = styled(Div)`
  border: 1px solid;
  box-sizing: border-box;
  border-radius: 3px;
  flex: none;
  order: 0;
  flex-grow: 0;
  margin: 5px 0;
  font-weight: ${typo.fontWeights.regular};
  display: inline-block;
  font-size: ${typo.smallText.mobileFontSize};
  padding: 4px 10px;
  color: ${props => props.theme.colorBodyFont};
`

interface CreditCardItemProps {
  className?: string
  creditCard: {
    id: string
    card: { brand: string; last4: string; type: number; country: string }
    isDefault: boolean
    type: number
  }
  defaultCreditCard
  onClickRadio: (x) => void
  onClickDelete?: (x) => void
  withActionIcons?: boolean
  Container?: any
  error?: string
  loading: boolean
}

const CreditCardItem = ({
  className,
  creditCard,
  defaultCreditCard = false,
  onClickRadio,
  onClickDelete,
  withActionIcons = false,
  Container = DefaultContainer,
  error,
  loading = true
}: CreditCardItemProps): JSX.Element => {
  const { t } = React.useContext(TranslationContext)
  const [cardInfo, setCardInfo] = React.useState({
    icon: IconCard,
    title: null
  })

  React.useMemo(() => {
    setCardInfo(getCreditCardInfo(creditCard.card.brand))
  }, [creditCard.card.brand])

  if (loading) {
    return <CreditCardSkeleton />
  }

  return (
    <Form.Radio
      className={className}
      value={creditCard.id}
      data-testid='creditCardItem'
      onClick={onClickRadio}
    >
      {({ checked, disabled }) => (
        <Flex flexDirection='column' width='100%'>
          <Container error={error && checked}>
            <Form.Radio.Dot checked={checked} disabled={disabled} />
            {withActionIcons && (
              <Flex marginLeft={marginSizes.medium}>
                <cardInfo.icon width={41} height={41} />
              </Flex>
            )}
            <Flex
              flexDirection='column'
              justifyContent='center'
              marginLeft={marginSizes.medium}
              marginRight={marginSizes.medium}
              alignItems='flex-start'
              flexGrow={1}
            >
              {defaultCreditCard && (
                <Div marginBottom={marginSizes.extraSmall}>
                  <StyledLabel dynamic>{t('Default')}</StyledLabel>
                </Div>
              )}
              {cardInfo.title && (
                <H4
                  marginTop={marginSizes.none}
                  marginBottom={marginSizes.none}
                >
                  {cardInfo.title.toUpperCase()} •••• {creditCard.card.last4}
                </H4>
              )}
            </Flex>
            <EndRightContainer>
              {withActionIcons ? (
                <DivButton
                  data-testid='creditCardItemDelete'
                  onClick={onClickDelete}
                  margin={marginSizes.none}
                >
                  <IconDelete iconSize={iconSizes.medium} />
                </DivButton>
              ) : (
                <Flex margin={marginSizes.none}>
                  <cardInfo.icon width={41} height={41} />
                </Flex>
              )}
            </EndRightContainer>
          </Container>
          {error && checked && <Form.FieldError>{error}</Form.FieldError>}
        </Flex>
      )}
    </Form.Radio>
  )
}

export default CreditCardItem
